import Big from 'big.js';

import { PropertiesSDK, UserDetailsDTO, UserLevelType } from '@investown/fe/api-sdk';

export type PropertiesTagColor = 'primary' | 'alert' | 'success' | 'warning';

export enum PropertyEndedInvestmentRoundStatus {
  PaidOff = 'PaidOff',
  PaymentOfPrincipal = 'PaymentOfPrincipal',
}

const mapPropertyStatusToTagColor: Record<PropertiesSDK.PropertyStatus, PropertiesTagColor> = {
  [PropertiesSDK.PropertyStatus.Active]: 'success',
  [PropertiesSDK.PropertyStatus.Inactive]: 'alert',
  [PropertiesSDK.PropertyStatus.Pending]: 'warning',
};

const mapPropertyInvestmentRoundStatusToTagColor: Record<
  PropertiesSDK.PropertyInvestmentRoundStatus,
  PropertiesTagColor | undefined
> = {
  [PropertiesSDK.PropertyInvestmentRoundStatus.Open]: 'success',
  [PropertiesSDK.PropertyInvestmentRoundStatus.Unknown]: undefined, // non-CF rounds
  [PropertiesSDK.PropertyInvestmentRoundStatus.Funded]: 'success',
  [PropertiesSDK.PropertyInvestmentRoundStatus.NotFunded]: 'alert',
  [PropertiesSDK.PropertyInvestmentRoundStatus.FullyInvested]: 'success',
  [PropertiesSDK.PropertyInvestmentRoundStatus.Ended]: 'alert',
};

export function getPropertyStatusColor({
  status,
  investmentAmountAvailable,
}: {
  status: PropertiesSDK.PropertyStatus;
  investmentAmountAvailable?: number;
}): PropertiesTagColor {
  if (status === PropertiesSDK.PropertyStatus.Active && !investmentAmountAvailable) {
    return 'alert';
  }
  return mapPropertyStatusToTagColor[status];
}

export function getPropertyStatusTranslationKey({
  status,
  investmentAmountAvailable,
}: {
  status: PropertiesSDK.PropertyStatus;
  investmentAmountAvailable?: number;
}): string {
  const baseKey = 'properties.status';
  if (status === PropertiesSDK.PropertyStatus.Active && !investmentAmountAvailable) {
    return `${baseKey}.FULL`;
  }
  return `${baseKey}.${status}`;
}

export function getPropertyInvestmentRoundStatusColor(
  status: PropertiesSDK.PropertyInvestmentRoundStatus
): PropertiesTagColor | undefined {
  return mapPropertyInvestmentRoundStatusToTagColor[status];
}

export function getEndedInvestmentRoundStatus(
  status: PropertiesSDK.PropertyInvestmentRoundStatus,
  isInvestmentRoundEnded?: boolean,
  isLegacyEnded?: boolean
): PropertyEndedInvestmentRoundStatus | undefined {
  if (isInvestmentRoundEnded && status === PropertiesSDK.PropertyInvestmentRoundStatus.Funded) {
    return PropertyEndedInvestmentRoundStatus.PaymentOfPrincipal;
  }
  if (isInvestmentRoundEnded && !isLegacyEnded && status === PropertiesSDK.PropertyInvestmentRoundStatus.Unknown) {
    return PropertyEndedInvestmentRoundStatus.PaymentOfPrincipal;
  }
  if (
    status === PropertiesSDK.PropertyInvestmentRoundStatus.Ended ||
    (isLegacyEnded && status === PropertiesSDK.PropertyInvestmentRoundStatus.Unknown)
  ) {
    return PropertyEndedInvestmentRoundStatus.PaidOff;
  }
  return undefined;
}

export function getPropertyInvestmentRoundStatusTranslationKey({
  status,
  repaymentStatus,
  isInvestmentRoundEnded,
}: {
  status: PropertiesSDK.PropertyInvestmentRoundStatus;
  repaymentStatus?: PropertiesSDK.PropertyInvestmentRoundRepaymentStatus;
  isInvestmentRoundEnded?: boolean;
}): string {
  if (
    repaymentStatus &&
    repaymentStatus !== PropertiesSDK.PropertyInvestmentRoundRepaymentStatus.Unknown &&
    !isInvestmentRoundEnded
  ) {
    return `property.currentInvestmentRound.repaymentStatus.${repaymentStatus}`;
  }
  return `property.currentInvestmentRound.status.${status}`;
}

interface PercentageAmountAvailableForInvestmentParams {
  investmentAmountAvailable: number;
  investmentAmountTotal: number;
  fraction: number;
}

export const percentageAmountAvailableForInvestment = ({
  investmentAmountAvailable,
  investmentAmountTotal,
  fraction,
}: PercentageAmountAvailableForInvestmentParams): number => {
  const bigInvestmentAmountAvailable = new Big(investmentAmountAvailable);
  const bigInvestmentAmountTotal = new Big(investmentAmountTotal);

  const investmentAmountDivided = bigInvestmentAmountAvailable.div(bigInvestmentAmountTotal);
  const availablePercentageAmount = investmentAmountDivided.times(100);

  return Number(availablePercentageAmount.toFixed(fraction, Big.roundUp));
};

export type PropertiesQueryItem = PropertiesSDK.PageablePropertiesQuery['items'][number];
export type PropertyInvestmentRound = Exclude<
  PropertiesSDK.PageablePropertiesQuery['items'][number]['currentInvestmentRound'],
  null | undefined
>;
export type SecondaryMarketOffer = Exclude<PropertiesSDK.SecondaryMarketOffer, null | undefined>;

export type SecondaryMarketBid = PropertiesSDK.SecondaryMarketBidsQuery['secondaryMarketBids'][number];

export const isUserPremium = (userDetails: UserDetailsDTO | undefined): boolean =>
  userDetails?.level.type === UserLevelType.Premium;

export const isPropertyPremium = (
  propertyInvestmentRound: Pick<PropertyInvestmentRound, 'level'> | undefined | null
): boolean => {
  if (!propertyInvestmentRound) {
    return false;
  }
  return propertyInvestmentRound.level === PropertiesSDK.PropertyInvestmentRoundLevel.Premium;
};

export const isPropertyLocked = (
  propertyInvestmentRound: PropertiesSDK.PropertyInvestmentRoundLevel | PropertiesSDK.SecondaryMarketItemLevel,
  premiumUser: boolean | undefined
): boolean => {
  if (
    !premiumUser &&
    (propertyInvestmentRound === PropertiesSDK.PropertyInvestmentRoundLevel.Premium ||
      propertyInvestmentRound === PropertiesSDK.SecondaryMarketItemLevel.Premium)
  ) {
    return true;
  }
  return false;
};

export const isSecondaryMarketOfferPremium = (level: PropertiesSDK.SecondaryMarketItemLevel): boolean => {
  if (!level) {
    return false;
  }
  return level === PropertiesSDK.SecondaryMarketItemLevel.Premium;
};

export const getPropertyBackgroundColor = (
  theme: { color: { [key: string]: string } },
  propertyIsPremium: boolean,
  userIsPremium?: boolean
): string => {
  if (!userIsPremium && propertyIsPremium) {
    return theme.color.bodyBackground;
  }

  if (userIsPremium && propertyIsPremium) {
    return theme.color.warningFaded;
  }

  return theme.color.contentBackground;
};

export const getPremiumLink = (locale: string | null): string =>
  `https://investown.${locale === 'cs' ? 'cz' : 'com'}/investown-premium`;

export const isCrowdfundingInvestmentRound = (propertyInvestmentRound: PropertyInvestmentRound): boolean =>
  propertyInvestmentRound.type === PropertiesSDK.PropertyInvestmentRoundType.Crowdfunding;

export const isPropertyHighlighted = (propertyInvestmentRound: PropertyInvestmentRound): boolean =>
  propertyInvestmentRound.status === PropertiesSDK.PropertyInvestmentRoundStatus.Open;
